<template>
    <div class="working-hours">
        <v-card :class="{ step1: workingHoursFormControl.step == 1, step2: workingHoursFormControl.step == 2 }" outlined>
            <v-card-title class="page-heading primary minh60">
                <div v-if="workingHoursFormControl.step == 1">
                    {{ $t('working_hours.heading') }}
                </div>
                <div v-else>
                    <v-btn
                        color="white"
                        icon
                        @click="workingHoursFormControl.step = 1"
                    >
                        <v-icon>fal fa-chevron-left</v-icon>
                    </v-btn>

                    {{ `${$t('general.working_hours')} (${formattedFrom_date} - ${formattedTo_date})` }}
                </div>
            </v-card-title>

            <v-card-text>
                <v-window v-model="workingHoursFormControl.step" touchless>
                    <v-window-item :value="1">
                        <v-form ref="workingHoursForm">
                            <v-btn-toggle
                                v-model="form.weekMonthOrDate"
                                class="my-6 wfull"
                                mandatory
                            >
                                <v-btn
                                    text
                                    class="flex-1"
                                    value="lastWeek"
                                    color="primary"
                                >
                                    {{ $t('general.last_week') }}
                                </v-btn>
                                <v-btn
                                    text
                                    class="flex-1"
                                    value="lastMonth"
                                    color="primary"
                                >
                                    {{ $t('general.last_month') }}
                                </v-btn>
                                <v-btn
                                    text
                                    class="flex-1"
                                    value="byDate"
                                    color="primary"
                                >
                                    {{ $t('general.by_date') }}
                                </v-btn>
                            </v-btn-toggle>

                            <v-slide-y-transition>
                                <v-row v-if="form.weekMonthOrDate == 'byDate'" no-gutters class="mb-7">
                                    <v-col
                                        cols="12"
                                        sm="6"
                                        :class="{ 'pr-1': $vuetify.breakpoint.smAndUp, 'pb-3': $vuetify.breakpoint.xs }"
                                    >
                                        <v-menu
                                            v-model="form.datePickerFrom"
                                            :close-on-content-click="false"
                                            offset-y
                                            :nudge-right="33"
                                            max-width="290px"
                                            min-width="290px"
                                            transition="scale-transition"
                                            :disabled="form.weekMonthOrDate != 'byDate'"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="formattedFrom_date"
                                                    :label="$t('general.start_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    readonly
                                                    :rules="form.rules.from_date"
                                                    :disabled="form.weekMonthOrDate != 'byDate'"
                                                    validate-on-blur
                                                    hide-details
                                                    v-on="on"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.from_date"
                                                :first-day-of-week="1"
                                                no-title
                                                :max="form.to_date"
                                                :disabled="form.weekMonthOrDate != 'byDate'"
                                                @input="form.datePickerFrom = false"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col
                                        cols="12"
                                        sm="6"
                                        :class="{ 'pl-1': $vuetify.breakpoint.smAndUp, 'pt-3': $vuetify.breakpoint.xs }"
                                    >
                                        <v-menu
                                            v-model="form.datePickerTo"
                                            :close-on-content-click="false"
                                            offset-y
                                            :nudge-right="33"
                                            max-width="290px"
                                            min-width="290px"
                                            transition="scale-transition"
                                            :disabled="form.weekMonthOrDate != 'byDate'"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="formattedTo_date"
                                                    :label="$t('general.end_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    readonly
                                                    :rules="form.rules.to_date"
                                                    :disabled="form.weekMonthOrDate != 'byDate'"
                                                    validate-on-blur
                                                    hide-details
                                                    v-on="on"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.to_date"
                                                :first-day-of-week="1"
                                                no-title
                                                :min="form.from_date"
                                                :disabled="form.weekMonthOrDate != 'byDate'"
                                                @input="form.datePickerTo = false"
                                            />
                                        </v-menu>
                                    </v-col>
                                </v-row>
                            </v-slide-y-transition>

                            <v-btn color="primary" depressed class="minw100 wfull" @click="generateReport()">
                                {{ $t('general.generate') }}
                            </v-btn>
                        </v-form>
                    </v-window-item>

                    <v-window-item :value="2">
                        <v-data-table
                            :headers="headers"
                            :items="working_hours"
                            :loading="loading"
                            :mobile-breakpoint="0"
                            disable-sort
                            disable-pagination
                            hide-default-footer
                            class="operations striped"
                        >
                            <template slot="item" slot-scope="props">
                                <tr>
                                    <td class="minw150">{{ props.item.name }}</td>
                                    <td class="maxw150">{{ props.item.totalHours }}</td>
                                    <td align="center" class="w35 maxw60">
                                        <v-btn
                                            v-if="props.item.totalHours != '/'"
                                            icon
                                            @click="viewSessions(props.item)"
                                        >
                                            <v-icon size="20" color="primary">fal fa-eye</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-window-item>
                </v-window>
            </v-card-text>
        </v-card>

        <cod-dialog :show.sync="workingHoursFormControl.sessionsViewFormVisible">
            <!-- Header -->
            <template slot="header">
                <span>{{ $t('general.working_hours') }}</span>
                <v-spacer />
                <v-btn text dark @click="$printService.printHtml('working-hours')" class="minw100">
                    <v-icon class="mr-3">fal fa-print</v-icon>
                    {{ $t('general.print') }}
                </v-btn>
            </template>

            <!-- Content -->
            <div slot="content" id="working-hours" class="py-2 working-hours-report">
                <div class="report-header">
                    <p>{{ getFormattedDate(form.from_date) + ' - ' + getFormattedDate(form.to_date) }}</p>
                    <p>{{ currentWaiter.name }}</p>
                </div>

                <table class="report-items">
                    <thead>
                        <tr>
                            <th align="left" width="200px">{{ $t('general.start') }}</th>
                            <th align="left" width="200px">{{ $t('general.end') }}</th>
                            <th align="right">{{ $t('general.duration') }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(session, index) in currentWaiter.sessions" :key="index">
                            <td align="left">{{ moment.utc(session.clock_in).local() | dateTimeHuman }}</td>
                            <td align="left">{{ moment.utc(session.clock_out).local() | dateTimeHuman }}</td>
                            <td align="right">{{ calculateDuration(session.clock_in, session.clock_out) }}</td>
                        </tr>

                        <tr class="divider">
                            <td colspan="4" />
                        </tr>

                        <tr class="summary-item">
                            <td align="left">{{ $t('general.working_hours') }}:</td>
                            <td align="right" colspan="3">
                                {{ currentWaiter.totalHours }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </cod-dialog>
    </div>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';

export default {
    components: {
        CodDialog
    },

    data() {
        return {
            working_hours: [],
            total: 0,
            loading: false,
            hours: 0,
            currentWaiter: {},

            form: new CodForm({
                weekMonthOrDate: 'lastWeek',
                datePickerFrom: false,
                datePickerTo: false,
                from_date: moment()
                    .subtract(1, 'weeks')
                    .startOf('isoWeek')
                    .format('YYYY-MM-DD'),
                to_date: moment()
                    .subtract(1, 'weeks')
                    .endOf('isoWeek')
                    .format('YYYY-MM-DD'),

                rules: {
                    from_date: [CodForm.rule('required')],
                    to_date: [CodForm.rule('required')]
                }
            }),

            workingHoursFormControl: {
                step: 1,
                sessionsViewFormVisible: false
            },

            headers: [
                { text: this.$tc('general.name', 1), value: 'name' },
                { text: this.$t('general.working_hours'), value: 'working_hours' },
                { text: this.$t('general.view'), sortable: false }
            ]
        };
    },

    computed: {
        formattedFrom_date() {
            if (this.form.from_date) {
                return this.getFormattedDate(this.form.from_date);
            } else {
                return null;
            }
        },

        formattedTo_date() {
            if (this.form.to_date) {
                return this.getFormattedDate(this.form.to_date);
            } else {
                return null;
            }
        },
    },

    methods: {
        // Convert date into desired format
        getFormattedDate(date) {
            return moment.utc(date, 'YYYY-MM-DD HH:mm:ss').local().format('DD.MM.YYYY.');
        },

        // Get paginated and sorted working hours from the server
        getWorkingHours() {
            return new Promise((resolve, reject) => {
                let url = `/admin/working-hours`;

                this.loading = true;

                let parameters = {};

                parameters.fromDate = `${this.formattedFrom_date}`;
                parameters.toDate = `${this.formattedTo_date}`;

                ApiService.get(url, {
                    params: parameters
                })
                    .then(response => {
                        this.loading = false;
                        this.working_hours = response.data.data;
                        this.total = response.data.total;

                        this.working_hours.forEach(item => {
                            let duration = 0;
                            item.sessions.forEach(session => {
                                duration += moment(session.clock_out).diff(moment(session.clock_in), 'seconds');
                            });
                            item.totalHours = '/';

                            let hours = Math.floor(duration / 3600);
                            let minutes = Math.floor((duration % 3600) / 60);
                            let seconds = (duration % 3600) % 60;

                            if (hours > 0) {
                                item.totalHours = `${hours}h`;
                            }

                            if (minutes > 0) {
                                if (item.totalHours === '/') {
                                    item.totalHours = `${minutes}m`;
                                } else {
                                    item.totalHours += ` ${minutes}m`;
                                }
                            }

                            if (seconds > 0) {
                                if (item.totalHours === '/') {
                                    item.totalHours = `${seconds}s`;
                                } else {
                                    item.totalHours += ` ${seconds}s`;
                                }
                            }
                        });
                        resolve();
                    })
                    .catch(error => console.log(error.message));
            });
        },

        // Generate report by specified criteria or return errors if there are any invalid inputs
        generateReport() {
            if (this.workingHoursFormControl.step === 1) {
                if (this.form.weekMonthOrDate === 'byDate') {
                    if (!this.formattedFrom_date || !this.formattedTo_date) {
                        this.$refs.workingHoursForm.validate();
                    } else {
                        this.getWorkingHours().then(() => (this.workingHoursFormControl.step = 2));
                    }
                } else {
                    this.getWorkingHours().then(() => (this.workingHoursFormControl.step = 2));
                }
            }
        },

        // View all of selected user's sessions
        viewSessions(waiter) {
            this.currentWaiter = waiter;
            this.workingHoursFormControl.sessionsViewFormVisible = true;
        },

        // Calculate session duration
        calculateDuration(clockIn, clockOut) {
            let difference = moment(clockOut).diff(moment(clockIn), 'seconds');
            let singleDayHours = '/';

            let hours = Math.floor(difference / 3600);
            let minutes = Math.floor((difference % 3600) / 60);
            let seconds = (difference % 3600) % 60;

            if (hours > 0) {
                singleDayHours = `${hours}h`;
            }

            if (minutes > 0) {
                if (singleDayHours === '/') {
                    singleDayHours = `${minutes}m`;
                } else {
                    singleDayHours += ` ${minutes}m`;
                }
            }

            if (seconds > 0) {
                if (singleDayHours === '/') {
                    singleDayHours = `${seconds}s`;
                } else {
                    singleDayHours += ` ${seconds}s`;
                }
            }

            return singleDayHours;
        }
    },

    watch: {
        'form.weekMonthOrDate': {
            handler() {
                if (this.form.weekMonthOrDate === 'lastWeek') {
                    this.form.from_date = moment()
                        .subtract(1, 'weeks')
                        .startOf('isoWeek')
                        .format('YYYY-MM-DD');
                    this.form.to_date = moment()
                        .subtract(1, 'weeks')
                        .endOf('isoWeek')
                        .format('YYYY-MM-DD');
                } else if (this.form.weekMonthOrDate === 'lastMonth') {
                    this.form.from_date = moment()
                        .subtract(1, 'months')
                        .startOf('month')
                        .format('YYYY-MM-DD');
                    this.form.to_date = moment()
                        .subtract(1, 'months')
                        .endOf('month')
                        .format('YYYY-MM-DD');
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.working-hours {
    .step1 {
        max-width: 600px;

        animation: opacity 0.5s;
        @keyframes opacity {
            0% {
                opacity: 0;
            }
            50% {
                opacity: 0;
            }
        }
    }

    .step2 {
        max-width: 600px;

        animation: width 0.5s;
        @keyframes width {
            0% {
                opacity: 0;
            }
            50% {
                opacity: 0;
            }
        }
    }

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }
}

.working-hours-report {
    font-size: 14px;
    color: black;
    -webkit-user-select: text;
    -webkit-app-region: none;

    p {
        padding: 2px 0 !important;
        margin: 0 !important;
    }

    .divider {
        border-top: 1px solid black;
        height: 1px !important;
    }

    .report-header {
        padding: 10px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
    }

    .report-items {
        border-collapse: collapse;
        width: 100%;

        thead {

            tr {
                border-top: 1px solid black;
                border-bottom: 1px solid black;
                height: 40px;
                font-size: 12px;

                th {
                    &:first-child {
                        padding: 0 10px;
                        max-width: 250px;
                    }

                    &:last-child {
                        padding-right: 10px;
                    }
                }
            }
        }

        tbody {

            tr {
                height: 30px;

                td {
                    &:first-child {
                        padding: 0 10px;
                        max-width: 250px;
                    }

                    &:last-child {
                        padding-right: 10px;
                    }
                }

                &:hover {
                    background: #f7f7f7;
                }
            }

            .summary-item {
                font-weight: 600;
            }
        }
    }
}
</style>
